/**
 * Where the visitor is, asked for only when it is needed.
 *
 * Two ways in:
 *
 * - quiet() never makes the browser ask and never spends anything. It gives
 *   the precise position when the visitor has already allowed it for this
 *   site, otherwise the approximate one. For anything that only uses a
 *   location if one is to hand, such as a map as the page loads.
 * - ask() is for when the visitor has just asked for something near them.
 *   In order:
 *     1. the browser's own location, which prompts if they have not answered
 *        yet: free and precise;
 *     2. Google's Geolocation API, when they refuse or the browser cannot
 *        tell. Treated as precise, so the visitor gets the nearest venues,
 *        but only when Google says it is within 10 km;
 *     3. the approximate position, when Google fails or is rougher than
 *        that, or once its daily quota is used up.
 *
 * Google is called straight from the browser with a key restricted to
 * Reevela's own domains, from Theme settings → Location → Google Geolocation
 * API key. A website cannot give it Wi-Fi or cell data, so it places the
 * visitor by their internet address, which for Danish addresses can be where
 * the provider routes traffic, tens of kilometres away. Its `accuracy` radius
 * says how rough the answer is; above 10 km it is treated as no answer.
 * Clearing the key skips this step.
 *
 *   POST https://www.googleapis.com/geolocation/v1/geolocate?key=<key>  { "considerIp": true }
 *     →  200 { "location": { "lat": 55.68, "lng": 12.57 }, "accuracy": 1500 }
 *
 * The approximate position is worked out by the Reevela server from the
 * visitor's internet address, the way an online map centres on your city
 * without asking: city-level, never a pin on a street, and free. It comes from
 * Theme settings → Location → Approximate location endpoint
 * (https://geo.reevela.com/storefront/approximate-location, behind CloudFront,
 * which supplies the visitor's location), called straight from the browser
 * rather than through the App Proxy, as the proxy would hide the visitor's
 * address. Clearing the setting turns it off.
 *
 *   GET <endpoint>  →  200 { "latitude": 55.68, "longitude": 12.57, "city": "Copenhagen", "country": "DK" }
 *                   →  204 when the address cannot be placed
 *
 * Both resolve to { lat, lng, approximate, source, city, country } or to null,
 * never reject. A precise position has no city or country, and Google's has
 * source "google". whenConsentChanges() reports the new position when the
 * visitor allows or takes back the location later.
 */
(function () {
  if (window.ReevelaLocation) return;

  var config = window.ReevelaLocationConfig || {};
  // Once per visit is enough for either lookup: an internet address does not
  // move between pages, and Google is paid per call.
  var APPROXIMATE_KEY = "reevela:approximate-location";
  var GOOGLE_KEY = "reevela:google-location";
  var GOOGLE_URL = "https://www.googleapis.com/geolocation/v1/geolocate?key=";
  var approximateRequest = null;
  var googleRequest = null;

  function readStored(key) {
    try {
      var stored = JSON.parse(window.sessionStorage.getItem(key) || "null");
      return stored && typeof stored.lat === "number" && typeof stored.lng === "number" ? stored : null;
    } catch (error) {
      return null;
    }
  }

  function store(key, place) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(place));
    } catch (error) {
      // Private modes can refuse storage; it is looked up again next page.
    }
  }

  function approximate() {
    if (approximateRequest) return approximateRequest;

    var stored = readStored(APPROXIMATE_KEY);
    if (stored) return (approximateRequest = Promise.resolve(stored));

    var endpoint = config.approximateEndpoint;
    if (!endpoint || !window.fetch) return Promise.resolve(null);

    approximateRequest = fetch(endpoint, { headers: { Accept: "application/json" }, credentials: "omit" })
      .then(function (response) {
        return response.ok && response.status !== 204 ? response.json() : null;
      })
      .then(function (data) {
        if (!data || typeof data.latitude !== "number" || typeof data.longitude !== "number") return null;
        var place = {
          lat: data.latitude,
          lng: data.longitude,
          approximate: true,
          city: data.city || "",
          country: data.country || "",
        };
        store(APPROXIMATE_KEY, place);
        return place;
      })
      .catch(function () {
        return null;
      });
    return approximateRequest;
  }

  // Google's answer is not used when it may be further off than this.
  var MAX_GOOGLE_ACCURACY_IN_METRES = 10000;

  // A missing or non-numeric accuracy counts as accurate enough, as does one
  // kept earlier in the visit from before accuracy was stored.
  function accurateEnough(place) {
    return typeof place.accuracy !== "number" || place.accuracy <= MAX_GOOGLE_ACCURACY_IN_METRES;
  }

  /**
   * Google's position for the visitor. Any failure — a quota used up, a key
   * the domain is not allowed to use, no answer — resolves to null, and is
   * not tried again on this page. So does an answer rougher than
   * MAX_GOOGLE_ACCURACY_IN_METRES, which is still kept for the visit so it is
   * not paid for again.
   */
  function google() {
    if (googleRequest) return googleRequest;

    var stored = readStored(GOOGLE_KEY);
    if (stored) {
      // Marked on the way out too, as one kept earlier in the visit may
      // predate the mark.
      stored.source = "google";
      return (googleRequest = Promise.resolve(accurateEnough(stored) ? stored : null));
    }

    var key = config.googleGeolocationKey;
    if (!key || !window.fetch) return Promise.resolve(null);

    googleRequest = fetch(GOOGLE_URL + encodeURIComponent(key), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({ considerIp: true }),
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        var location = data && data.location;
        if (!location || typeof location.lat !== "number" || typeof location.lng !== "number") return null;
        // Precise enough for the nearest venues, not for a distance to the
        // hundred metres: it comes from the internet address.
        var place = {
          lat: location.lat,
          lng: location.lng,
          approximate: false,
          source: "google",
          city: "",
          country: "",
          accuracy: typeof data.accuracy === "number" ? data.accuracy : null,
        };
        store(GOOGLE_KEY, place);
        return accurateEnough(place) ? place : null;
      })
      .catch(function () {
        return null;
      });
    return googleRequest;
  }

  // One read at a time: answering the prompt also fires the permission
  // change, which would otherwise read the same position again.
  var browserRequest = null;

  function browser() {
    if (browserRequest) return browserRequest;
    browserRequest = readBrowser().then(function (place) {
      browserRequest = null;
      return place;
    });
    return browserRequest;
  }

  function readBrowser() {
    return new Promise(function (resolve) {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        function (position) {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            approximate: false,
            city: "",
            country: "",
          });
        },
        function () {
          resolve(null);
        },
        { timeout: 10000, maximumAge: 300000 },
      );
    });
  }

  // 'granted', 'denied', 'prompt', or 'unknown' where the browser will not say.
  function permission() {
    if (!navigator.permissions || !navigator.permissions.query) return Promise.resolve("unknown");
    return navigator.permissions
      .query({ name: "geolocation" })
      .then(function (status) {
        return status.state;
      })
      .catch(function () {
        return "unknown";
      });
  }

  function quiet() {
    return permission()
      .then(function (state) {
        return state === "granted" ? browser() : null;
      })
      .then(function (place) {
        return place || approximate();
      });
  }

  function ask() {
    return browser()
      .then(function (place) {
        return place || google();
      })
      .then(function (place) {
        return place || approximate();
      });
  }

  /**
   * Calls back with where the visitor now is when they change their mind
   * about the location while the page is open, e.g. from the address bar:
   *
   * - allowed after refusing: the browser's position, so a page showing
   *   Google's or the estimated one can switch to the precise one;
   * - taken back after allowing: what refusing gives, Google's position or,
   *   failing that, the estimate.
   *
   * Browsers without the permission change event never call back.
   */
  var consentListeners = [];
  var watchingPermission = false;

  function whenConsentChanges(callback) {
    consentListeners.push(callback);
    if (watchingPermission || !navigator.permissions || !navigator.permissions.query) return;
    watchingPermission = true;

    navigator.permissions
      .query({ name: "geolocation" })
      .then(function (status) {
        // Whatever the change, the answer for where it now stands; the
        // listener decides whether that differs from what it shows.
        function changed() {
          var lookup =
            status.state === "granted"
              ? browser()
              : google().then(function (place) {
                  return place || approximate();
                });
          lookup.then(function (place) {
            if (!place) return;
            consentListeners.forEach(function (listener) {
              listener(place);
            });
          });
        }
        if (status.addEventListener) status.addEventListener("change", changed);
        else status.onchange = changed;
      })
      .catch(function () {
        // No permission events here; the next ask picks it up instead.
      });
  }

  window.ReevelaLocation = {
    quiet: quiet,
    ask: ask,
    approximate: approximate,
    // Google's position on its own, for where the city-level estimate is too
    // rough, such as recording where a new venue is.
    google: google,
    // 'granted', 'denied', 'prompt' or 'unknown', without asking: for deciding
    // whether offering to use the location is worth it.
    permission: permission,
    whenConsentChanges: whenConsentChanges,
  };
})();
